/**
 * Data hooks for the Courier Operations feature.
 *
 * Bundles every cache-bound interaction the page needs into one module so the
 * UI layer stays presentational and feature tests can mock the hooks with
 * plain objects. All hooks are typed against the generated OpenAPI surface —
 * no `as never` casts are required because every endpoint we touch
 * (`/admin/couriers`, `/admin/couriers/{id}/deliveries`, the per-delivery
 * accept/reject/status mutations, reviews, availability and tracking) is
 * already in `paths`.
 *
 * The query-key factory keeps cache invalidations consistent: every mutation
 * invalidates `courierOpsKeys.all`, which is enough granularity for the
 * volumes this admin panel sees.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@favelafood/shared/api/client';
import type { components } from '@favelafood/shared/api/generated/api';

type CourierResponse = components['schemas']['CourierResponse'];
type CourierPage = components['schemas']['PageResponseCourierResponse'];
type DeliveryResponse = components['schemas']['DeliveryResponse'];
type DeliveryPage = components['schemas']['PageResponseDeliveryResponse'];
type ReviewPage = components['schemas']['PageResponseReviewResponse'];
type DeliveryTracking = components['schemas']['DeliveryTrackingResponse'];
type AvailabilityStatus = components['schemas']['AvailabilityStatusEnum'];
type DeliveryStatus = components['schemas']['DeliveryStatusEnum'];

export interface DeliveriesFilters {
  /** UI-only filter — backend list does not accept `status`, so it's applied client-side. */
  status?: DeliveryStatus;
  page: number;
  size: number;
}

/** Narrow status set used in the deliveries filter UI. */
export const DELIVERY_STATUSES: readonly DeliveryStatus[] = [
  'ASSIGNED',
  'PICKED_UP',
  'ON_THE_WAY',
  'DELIVERED',
  'CANCELLED',
] as const;

/** TanStack Query key factory — keeps cache invalidations consistent. */
export const courierOpsKeys = {
  all: ['courier-ops'] as const,
  couriers: (params: { page: number; size: number }) =>
    ['courier-ops', 'couriers', params] as const,
  deliveries: (courierId: string, filters: DeliveriesFilters) =>
    ['courier-ops', 'deliveries', courierId, filters] as const,
  reviews: (courierId: string, page: number, size: number) =>
    ['courier-ops', 'reviews', courierId, page, size] as const,
  tracking: (deliveryId: string) =>
    ['courier-ops', 'tracking', deliveryId] as const,
};

const unwrap = <T,>(data: T | undefined, error: unknown): T => {
  if (error) throw error as Error;
  if (data === undefined) throw new Error('Resposta vazia do servidor');
  return data;
};

/* -------------------------------------------------------------------------- */
/* Couriers list (used by selector)                                           */
/* -------------------------------------------------------------------------- */

export function useAdminCouriers(params: {
  page: number;
  size: number;
  status?: AvailabilityStatus;
}): UseQueryResult<CourierPage, Error> {
  return useQuery<CourierPage, Error>({
    queryKey: courierOpsKeys.couriers({ page: params.page, size: params.size }),
    queryFn: async () => {
      const query: Record<string, unknown> = { page: params.page, size: params.size };
      if (params.status) query.status = params.status;
      const { data, error } = await api.GET('/api/v1/admin/couriers', {
        params: {
          query: query as { page?: number; size?: number; status?: AvailabilityStatus },
        },
      });
      return unwrap(data, error);
    },
  });
}

/* -------------------------------------------------------------------------- */
/* Deliveries list                                                            */
/* -------------------------------------------------------------------------- */

export function useCourierDeliveries(
  courierId: string | null,
  filters: DeliveriesFilters,
): UseQueryResult<DeliveryPage, Error> {
  return useQuery<DeliveryPage, Error>({
    queryKey: courierOpsKeys.deliveries(courierId ?? 'none', filters),
    enabled: courierId !== null,
    queryFn: async () => {
      if (!courierId) throw new Error('Selecione um entregador');
      const { data, error } = await api.GET('/api/v1/admin/couriers/{id}/deliveries', {
        params: {
          path: { id: courierId },
          query: { page: filters.page, size: filters.size },
        },
      });
      return unwrap(data, error);
    },
  });
}

/* -------------------------------------------------------------------------- */
/* Reviews list                                                               */
/* -------------------------------------------------------------------------- */

export function useCourierReviews(
  courierId: string | null,
  page: number,
  size: number,
): UseQueryResult<ReviewPage, Error> {
  return useQuery<ReviewPage, Error>({
    queryKey: courierOpsKeys.reviews(courierId ?? 'none', page, size),
    enabled: courierId !== null,
    queryFn: async () => {
      if (!courierId) throw new Error('Selecione um entregador');
      const { data, error } = await api.GET('/api/v1/admin/couriers/{id}/reviews', {
        params: { path: { id: courierId }, query: { page, size } },
      });
      return unwrap(data, error);
    },
  });
}

/* -------------------------------------------------------------------------- */
/* Tracking                                                                   */
/* -------------------------------------------------------------------------- */

export function useDeliveryTracking(
  deliveryId: string | null,
): UseQueryResult<DeliveryTracking, Error> {
  return useQuery<DeliveryTracking, Error>({
    queryKey: courierOpsKeys.tracking(deliveryId ?? 'none'),
    enabled: deliveryId !== null,
    queryFn: async () => {
      if (!deliveryId) throw new Error('Selecione uma entrega');
      const { data, error } = await api.GET('/api/v1/deliveries/{id}/tracking', {
        params: { path: { id: deliveryId } },
      });
      return unwrap(data, error);
    },
  });
}

/* -------------------------------------------------------------------------- */
/* Mutations                                                                  */
/* -------------------------------------------------------------------------- */

interface AcceptVars {
  courierId: string;
  deliveryId: string;
}
interface RejectVars extends AcceptVars {
  reason?: string;
}
interface UpdateStatusVars {
  deliveryId: string;
  status: DeliveryStatus;
}

export function useAcceptDelivery(): UseMutationResult<DeliveryResponse, Error, AcceptVars> {
  const qc = useQueryClient();
  return useMutation<DeliveryResponse, Error, AcceptVars>({
    mutationFn: async ({ courierId, deliveryId }) => {
      const { data, error } = await api.POST(
        '/api/v1/admin/couriers/{courierId}/deliveries/{deliveryId}/accept',
        { params: { path: { courierId, deliveryId } } },
      );
      return unwrap(data, error);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: courierOpsKeys.all });
    },
  });
}

export function useRejectDelivery(): UseMutationResult<void, Error, RejectVars> {
  const qc = useQueryClient();
  return useMutation<void, Error, RejectVars>({
    mutationFn: async ({ courierId, deliveryId, reason }) => {
      const { error } = await api.POST(
        '/api/v1/admin/couriers/{courierId}/deliveries/{deliveryId}/reject',
        {
          params: { path: { courierId, deliveryId } },
          body: { reason: reason ?? '' },
        },
      );
      if (error) throw error as Error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: courierOpsKeys.all });
    },
  });
}

export function useUpdateDeliveryStatus(): UseMutationResult<
  DeliveryResponse,
  Error,
  UpdateStatusVars
> {
  const qc = useQueryClient();
  return useMutation<DeliveryResponse, Error, UpdateStatusVars>({
    mutationFn: async ({ deliveryId, status }) => {
      const { data, error } = await api.PATCH(
        '/api/v1/admin/couriers/deliveries/{deliveryId}/status',
        {
          params: { path: { deliveryId } },
          body: { status },
        },
      );
      return unwrap(data, error);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: courierOpsKeys.all });
    },
  });
}

export function useToggleAvailability(): UseMutationResult<CourierResponse, Error, string> {
  const qc = useQueryClient();
  return useMutation<CourierResponse, Error, string>({
    mutationFn: async (courierId) => {
      const { data, error } = await api.PATCH('/api/v1/admin/couriers/{id}/availability', {
        params: { path: { id: courierId } },
      });
      return unwrap(data, error);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: courierOpsKeys.all });
    },
  });
}
